import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronLeft,
  Sparkles,
  ArrowUpRight,
  CheckCircle2,
  Compass,
  Users,
  ArrowRight,
  MapPin,
  Award,
  Quote,
  Calendar,
  Layers,
  Zap,
  ShieldCheck,
  Star,
  Flame,
  Binary,
  Sprout,
  GraduationCap,
  Rocket,
  Gift
} from 'lucide-react';

// 3 Phases Data
const PHASES = [
  {
    number: "Phase 01",
    title: "Startup Onboarding & Intern Integration",
    timeline: "Months 1 - 4",
    status: "Completed",
    description: "Successfully onboarded the first 200+ pioneering startups under Startup Odisha, capturing and fulfilling their immediate intern hiring requirements while integrating real-time state grant alerts and state-wide college campaign distributions.",
    highlights: [
      "Onboarded and registered the first 200+ active state-wide startups into our automated talent matching systems.",
      "Fulfilled 100% of core junior and technical internship hiring requirements for early teams.",
      "Established notification pipelines keeping all registered founders continuously updated with State Startup Grants and incubator events.",
      "Shared and launched direct job posting campaigns targeting elite and regional engineering & management colleges based in Odisha."
    ],
    color: "from-orange-500 to-amber-500",
    bgAccent: "bg-orange-50 text-orange-700 border-orange-100",
    icon: Compass
  },
  {
    number: "Phase 02",
    title: "Local Talent Pipeline & Regional Hiring Drive",
    timeline: "Months 5 - 8",
    status: "Upcoming",
    description: "Formulating a visionary expansion roadmap to onboard an additional 500+ high-growth startups to dramatically boost student and graduate employment across Odisha. Our focus is actively creating dedicated career pipelines centering on local talent and fostering regional hiring campaigns.",
    highlights: [
      "On track to onboard and empower 500+ next-generation Odisha startups on our unified hiring registry.",
      "Designing highly-curated pipelines connecting qualified local candidates directly to tier-2 and tier-3 district offices.",
      "Boosting overall state placement rates by promoting regional, local-first hiring initiatives directly with startup founders.",
      "Encouraging structured internships and training bootcamps to retain Odisha's top-tier tech minds within the state."
    ],
    color: "from-blue-600 to-teal-500",
    bgAccent: "bg-blue-50 text-blue-700 border-blue-100",
    icon: Layers
  },
  {
    number: "Phase 03",
    title: "Full-Scale Onboarding & Retention Tracking",
    timeline: "Months 9 - Ongoing",
    status: "Upcoming",
    description: "Continuous synchronization of dynamic placements, on-the-job internship conversions, and feedback loops to enhance local workforce value.",
    highlights: [
      "Planning to integrate full-time roles and apprenticeships for over 2,000+ Startup employees.",
      "Planning to implement structured 6-month feedback loop surveys with founders.",
      "Planning to partner with state institutions to scale sustainable local incubation."
    ],
    color: "from-purple-600 to-pink-500",
    bgAccent: "bg-purple-50 text-purple-700 border-purple-100",
    icon: Rocket
  }
];

// Startups reviews & logos
const TESTIMONIALS = [
  {
    id: 1,
    startupName: "BabyCue Private Limited",
    founders: "Manish Kumar Swain",
    role: "Founder & CEO",
    review: "Talent Connect transformed our hiring speed. We onboarded 12 interns from Rourkela who are already helping build our crop monitoring system. The focus on tier-2 engineering talent was exactly what we needed.",
    rating: 5,
    city: "Bhubaneswar",
    startupSector: "Agritech",
    logoColor: "bg-emerald-500",
    logoText: "AA",
    icon: Sprout,
    stats: "15 placements"
  },
  {
    id: 2,
    startupName: "Amo Paribesh Solutions",
    founders: "Rashmi Ranjan Behera",
    role: "Founder & MD",
    review: "We were looking for junior machine learning engineers who could hit the ground running. Through the curated Startup Odisha bootcamps, we found 4 outstanding hires who understand scalable systems completely.",
    rating: 5,
    city: "Cuttack",
    startupSector: "Deep Tech / AI",
    logoColor: "bg-indigo-600",
    logoText: "KA",
    icon: Binary,
    stats: "6 placements"
  },
  {
    id: 3,
    startupName: "Sparrow Campus",
    founders: "Prasanna Panda",
    role: "Founder & CEO",
    review: "As an early-stage startup, securing top-tier Insider Sales Executive on a budget was a nightmare. Talent Connect solved this by connecting us with highly competent graduates who hold specialized security degrees from Odisha.",
    rating: 5,
    city: "Bhubaneswar",
    startupSector: "Fintech",
    logoColor: "bg-cyan-500",
    logoText: "MF",
    icon: ShieldCheck,
    stats: "8 placements"
  },
  {
    id: 4,
    startupName: "EKATRAA ASSISTANCE PRIVATE LIMITED",
    founders: "Durga Prasad Rath",
    role: "Founder & Director",
    review: "The ecosystem mapping was brilliant. We hired diploma engineers from regional colleges who brought incredible enthusiasm and hands-on skills to our solar installation projects. Zero retraining needed.",
    rating: 5,
    city: "Sambalpur",
    startupSector: "CleanTech & IoT",
    logoColor: "bg-amber-500",
    logoText: "OC",
    icon: Flame,
    stats: "22 placements"
  },
  {
    id: 5,
    startupName: "Biswambhar Logistics Private Limited",
    founders: "Soumya Kanta Sahoo",
    role: "Founder & Director ",
    review: "Our mission to bring bilingual learning content to Odisha's rural colleges needed high-empathy sales and QA teams. Talent Connect helped us hire 6 incredibly motivated people from local colleges in under four weeks.",
    rating: 5,
    city: "Puri",
    startupSector: "EdTech",
    logoColor: "bg-rose-500",
    logoText: "CE",
    icon: GraduationCap,
    stats: "14 placements"
  },
  {
    id: 6,
    startupName: "Integrated Natural Farms Private Limited",
    founders: "SIDDHESH KUMAR SAHU",
    role: "Founder",
    review: "A truly professional placement experience. The matching engine identified candidates who had done relevant project work in industrial IoT. Talent Connect is doing a fantastic job empowering Odisha's talent.",
    rating: 5,
    city: "Jharsuguda",
    startupSector: "Industrial IoT",
    logoColor: "bg-slate-700",
    logoText: "UA",
    icon: Zap,
    stats: "10 placements"
  }
];

export const StartupOdisha: React.FC = () => {
  const navigate = useNavigate();
  const [activePhase, setActivePhase] = useState<number>(0);

  const filteredTestimonials = TESTIMONIALS;

  return (
    <div className="min-h-screen bg-slate-50/50 py-12 sm:py-8 relative overflow-hidden">
      {/* Abstract Design accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-orange-200/30 to-indigo-200/20 rounded-full blur-3xl -mr-40 -mt-20 pointer-events-none"></div>
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-gradient-to-tr from-blue-200/20 to-pink-200/30 rounded-full blur-3xl -ml-40 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-8 sm:px-4 lg:px-6 relative z-10">

        {/* Navigation Breadcrumb */}
        <div className="mb-10 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="group flex items-center gap-2 text-slate-600 hover:text-brand-600 font-bold transition-colors bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-100"
          >
            <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </button>

          <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-100 rounded-full text-xs font-bold text-orange-600">
            <Award className="w-4 h-4 animate-bounce" /> Official Collaboration Page
          </div>
        </div>

        {/* Hero Section */}
        <section className="mb-20 bg-slate-900 rounded-[2.5rem] p-8 xsm:py-8 xsm:px-4 text-white  relative overflow-hidden">
          <div className="absolute right-0 bottom-0 w-1/3 h-full bg-cover opacity-10 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80')] xsm:hidden"></div>
          {/* Saffron Glowing ring representing Startup Odisha flag colors */}
          <div className="absolute top-0 right-1/4 w-80 h-80 bg-gradient-to-tr from-orange-600 to-amber-500 rounded-full blur-[100px] opacity-20 -mr-40 -mt-20 xsm:hidden"></div>

          <div className="relative z-10 max-w-4xl">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-black uppercase tracking-widest rounded-full mb-6 shadow-md shadow-orange-500/10">
              <Building2 className="w-4 h-4" /> STATE COLLABORATION DRIVE
            </span>

            <h1 className="text-6xl xsm:text-3xl sm:text-4xl font-black tracking-tight leading-tight mb-6">
              Empowering Odisha's Next-Gen <br />
              <span className="text-orange-400">
                Startup Ecosystem
              </span>
            </h1>

            <p className="text-xl xsm:text-sm sm:text-ld text-slate-300 leading-relaxed font-medium mb-10 max-w-3xl ">
              Talent Connect has partnered with <span className="text-orange-400">Startup Odisha</span> to bridge the technology gap. Together, we have successfully collaborated with <span className="text-orange-400">2,000+ pioneering startups</span> across the state over <span className="text-orange-400">3 structured operational phases</span> to place high-performing college minds and build the future of Indian deeptech.
            </p>

            {/* Quick Metrics */}
            <div className="flex flex-row xsm:flex-wrap  xsm:items-start justify-between items-center gap-6 bg-slate-950/40 p-6 rounded-2xl border border-slate-750/30 backdrop-blur-sm ">
              <div>
                <p className="text-slate-400 font-bold text-xs uppercase tracking-wider mb-1">State Startups</p>
                <h4 className="text-4xl sm:text-3xl font-extrabold text-white">2,000+</h4>
              </div>
              <div>
                <p className="text-slate-400 font-bold text-xs uppercase tracking-wider mb-1">Launch Phases</p>
                <h4 className="text-4xl sm:text-3xl font-extrabold text-orange-400">3 Phases</h4>
              </div>
              <div>
                <p className="text-slate-400 font-bold text-xs uppercase tracking-wider mb-1">Impacted Districts</p>
                <h4 className="text-4xl sm:text-3xl font-extrabold text-teal-400">30+</h4>
              </div>
              <div>
                <p className="text-slate-400 font-bold text-xs uppercase tracking-wider mb-1">Jobs Facilitated</p>
                <h4 className="text-4xl sm:text-3xl font-extrabold text-brand-400">5,000+</h4>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Partner Logos section */}
        <section className="mb-20 text-center">
          <p className="text-slate-500 font-bold text-xs uppercase tracking-[0.2em] mb-6">In Association with apex bodies</p>
          <div className="flex flex-wrap justify-center items-center gap-10 bg-white px-8 py-6 rounded-2xl shadow-sm border border-slate-100 max-w-5xl mx-auto">
            <div className="flex items-center gap-2 opacity-85 hover:opacity-100 transition">
              <div className="p-2.5 bg-orange-100 text-orange-650 rounded-xl">
                <Building2 className="w-6 h-6" />
              </div>
              <span className="text-slate-800 font-black tracking-tight text-lg sm:text-base">STARTUP ODISHA</span>
            </div>
            <div className="h-6 w-px bg-slate-200 hidden sm:block"></div>
            <div className="flex items-center gap-2 opacity-85 hover:opacity-100 transition">
              <div className="p-2.5 bg-brand-100 text-brand-650 rounded-xl">
                <Users className="w-6 h-6" />
              </div>
              <span className="text-slate-800 font-black tracking-tight text-lg sm:text-base">TALENT CONNECT</span>
            </div>
            <div className="h-6 w-px bg-slate-200 hidden sm:block"></div>
            <div className="flex items-center gap-2 opacity-85 hover:opacity-100 transition">
              <div className="p-2.5 bg-slate-100 text-slate-750 rounded-xl">
                <Award className="w-6 h-6" />
              </div>
              <span className="text-slate-800 font-black tracking-tight text-lg sm:text-base">MSME DEPT. ODISHA</span>
            </div>
          </div>
        </section>

        {/* 3 Phases Detailed Showcase */}
        <section className="mb-20">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-4xl sm:text-3xl font-black text-slate-950 tracking-tight mb-4">
              Our Structured 3-Phase Integration
            </h2>
            <p className="text-slate-600 font-medium">
              We mapped talent pools, trained aspirants through intensive bootcamps, and synchronized standard hiring funnels to scale regional innovation.
            </p>
          </div>

          {/* Phase selector tabs */}
          <div className="flex gap-4 xsm:gap-2  xsm:flex-wrap xsm:justify-center max-w-4xl mx-auto mb-10 bg-slate-100 p-1.5 rounded-2xl">
            {PHASES.map((phase, idx) => (
              <button
                key={idx}
                onClick={() => setActivePhase(idx)}
                className={`flex-1 py-4 sm:py-3 h-[50px]  px-4 rounded-xl text-sm sm:text-sm font-black tracking-tight transition-all duration-300 flex flex-row sm:flex-row items-center justify-center gap-2 ${activePhase === idx
                  ? 'bg-slate-900 text-white shadow-md shadow-slate-900/10 scale-105'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/55'
                  }`}
              >
                <span className={`w-2 h-2 rounded-full  ${activePhase === idx ? 'bg-orange-400 animate-pulse' : 'bg-slate-400'}`}></span>
                <span className="opacity-75 xsm:hidden font-bold sm:inline">{phase.number}:</span> {phase.title.split('&')[0].split(' ')[0]}
              </button>
            ))}
          </div>

          {/* Phase Panel card */}
          <div className="bg-white rounded-3xl p-8 sm:p-6 xsm:p-4 border border-slate-100 shadow-xl relative overflow-hidden">
            {/* Background design graphic */}
            <div className={`absolute top-0 right-0 w-72 h-72 bg-gradient-to-br ${PHASES[activePhase].color} opacity-[0.03] rounded-full blur-3xl`}></div>

            <div className="grid grid-cols-2 xsm:grid-cols-1 gap-8 items-center w-full">

              {/* Info Column */}
              <div className="w-full">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${PHASES[activePhase].bgAccent}`}>
                    <Calendar className="w-3.5 h-3.5" />
                    {PHASES[activePhase].timeline}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 bg-teal-50 border border-teal-100 rounded-full text-xs font-bold text-teal-700`}>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {PHASES[activePhase].status}
                  </span>
                </div>

                <h3 className="text-3xl sm:text-2xl font-black text-slate-900 mb-4 tracking-tight break-words">
                  <span className="text-orange-500 font-extrabold mr-2">{PHASES[activePhase].number}.</span>
                  {PHASES[activePhase].title}
                </h3>

                <p className="text-slate-600 font-medium mb-6 leading-relaxed break-words">
                  {PHASES[activePhase].description}
                </p>

                <div className="space-y-3">
                  <p className="text-slate-950 font-bold text-sm tracking-wide uppercase">Core Milestones Reached:</p>
                  {PHASES[activePhase].highlights.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="mt-1 bg-emerald-50 text-emerald-600 p-0.5 rounded-full border border-emerald-100 flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <span className="text-slate-700 font-medium text-sm leading-relaxed break-words">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Graphic Icon Display Column */}
              <div className="w-full flex justify-center">
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-tr ${PHASES[activePhase].color} opacity-20 rounded-[2rem] blur-xl rotate-6`}></div>

                  <div className="relative bg-slate-900 text-white w-80 h-80 xsm:w-48 xsm:h-48 rounded-[2rem] flex flex-col items-center justify-center p-8 xsm:p-4 text-center border border-slate-800 shadow-2xl">
                    <div className="p-4 xsm:p-3 bg-slate-800 rounded-2xl mb-4 xsm:mb-3 text-orange-400 ring-4 ring-slate-850">
                      {React.createElement(PHASES[activePhase].icon, { className: "w-10 h-10 xsm:w-8 xsm:h-8" })}
                    </div>
                    <span className="text-xs xsm:text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">Active Phase</span>
                    <h4 className="text-2xl xsm:text-lg font-black text-white">{PHASES[activePhase].number}</h4>
                    <span className="text-sm xsm:text-xs font-semibold text-slate-300 mt-2">{PHASES[activePhase].timeline}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Testimonials and Reviews Section */}
        <section className="mb-20">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div className="max-w-2xl">
              <span className="text-brand-650 font-black text-xs uppercase tracking-widest mb-2 block">FOUNDER REVIEWS</span>
              <h2 className="text-4xl sm:text-3xl font-black text-slate-900 tracking-tight">
                Startup Success & Testimonials
              </h2>
              <p className="text-slate-600 font-medium mt-2">
                Real feedback from collaborated founders of Startup Odisha. They hired, certified, and scaled teams with Talent Connect.
              </p>
            </div>
          </div>

          {/* Testimonial grid */}
          <div className="grid grid-cols-3 sm:grid-cols-2 xsm:grid-cols-1 gap-8">
            {filteredTestimonials.map((item) => (
              <div
                key={item.id}
                className="group relative bg-white rounded-3xl p-8 sm:p-6 border border-slate-100 shadow-[0_4px_25px_rgba(0,0,0,0.02)] hover:shadow-xl hover:border-orange-200/50 hover:-translate-y-1 transition-all duration-300 flex flex-col h-full"
              >
                {/* Accent line top */}
                <div className={`absolute top-0 left-8 right-8 h-1 bg-gradient-to-r ${PHASES[activePhase].color} opacity-0 group-hover:opacity-100 rounded-b-full transition-opacity duration-300`}></div>

                {/* Quote decoration */}
                <div className="absolute top-6 right-6 opacity-5 group-hover:opacity-10 text-slate-900 transition-opacity">
                  <Quote className="w-12 h-12" />
                </div>

                {/* Rating */}
                <div className="flex items-center gap-1 text-amber-500 mb-4">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-500" />
                  ))}
                </div>

                {/* Text */}
                <p className="text-slate-600 font-medium text-sm leading-relaxed mb-6 italic flex-grow">
                  "{item.review}"
                </p>

                {/* Founder Info & Logo */}
                <div className="flex items-center gap-4 pt-4 border-t border-dashed border-slate-100">
                  <div className={`w-12 h-12 rounded-2xl ${item.logoColor} text-white flex items-center justify-center font-black shadow-md flex-shrink-0 relative group-hover:scale-105 transition-transform`}>
                    {React.createElement(item.icon, { className: "w-5 h-5" })}
                    {/* Startup logo placeholder accent */}
                    <span className="absolute -bottom-1 -right-1 bg-white border border-slate-100 text-slate-900 font-black rounded text-[8px] px-1 shadow-sm">
                      {item.logoText}
                    </span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <h4 className="font-extrabold text-slate-900 text-sm truncate">{item.startupName}</h4>
                    <p className="text-xs text-slate-500 truncate">{item.founders} • {item.role}</p>
                  </div>
                </div>

                {/* Tag & City badges */}
                <div className="flex items-center justify-between mt-4 pt-2">
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    {item.city}
                  </span>
                  <span className="px-2.5 py-1 bg-slate-50 hover:bg-slate-100 text-[10px] font-bold text-slate-600 rounded-lg border border-slate-100">
                    {item.stats}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filteredTestimonials.length === 0 && (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-150">
              <p className="text-slate-500 font-bold">No startup reviews found for the selected filter.</p>
            </div>
          )}
        </section>

        {/* State-wide Regional Impact Hubs map */}
        <section className="mb-20">
          <div className="bg-slate-900 rounded-[2.5rem] p-8 xsm:px-6 text-white relative overflow-hidden border border-slate-800 shadow-2xl">
            <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/20 rounded-full blur-[100px] pointer-events-none -mr-40"></div>

            <div className="grid grid-cols-2 xsm:grid-cols-1 gap-8 items-center">

              <div className="col-span-1">
                <span className="text-orange-400 font-black text-xs uppercase tracking-widest mb-2 block">TALENT PIPELINE</span>
                <h3 className="text-3xl font-black text-white tracking-tight mb-4 leading-tight">
                  Regional Impact Hubs in Odisha
                </h3>
                <p className="text-slate-350 font-medium text-sm leading-relaxed mb-6">
                  Driving decentralization of high-tech jobs across Odisha. We connect directly with engineering institutes and management schools in key tech sectors.
                </p>

                <div className="space-y-4">
                  <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700/30 flex items-start gap-3">
                    <div className="p-2 bg-slate-700/60 text-orange-400 rounded-xl font-bold text-sm">BBSR</div>
                    <div>
                      <h5 className="font-extrabold text-white text-sm">Bhubaneswar (Capital Hub)</h5>
                      <p className="text-xs text-slate-400 font-medium">Fintech, E-commerce, AI/ML, and SaaS start-ups.</p>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700/30 flex items-start gap-3">
                    <div className="p-2 bg-slate-700/60 text-blue-400 rounded-xl font-bold text-sm">ROU</div>
                    <div>
                      <h5 className="font-extrabold text-white text-sm">Rourkela (Core & deeptech)</h5>
                      <p className="text-xs text-slate-400 font-medium">IoT development, industrial engineering, clean energy.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-1 bg-slate-950/40 border border-slate-850 p-6 xsm:p-3 rounded-3xl relative">
                {/* Stylized mockup map showcase */}
                <div className="absolute inset-0 bg-[radial-gradient(#ffffff0a_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none"></div>

                <h4 className="font-extrabold text-white text-base mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 bg-orange-500 rounded-full animate-ping"></span>
                  Placements Distribution across 30 Districts
                </h4>

                <div className="space-y-4">
                  {/* BBSR bar */}
                  <div>
                    <div className="flex justify-between items-center text-xs font-bold text-slate-300 mb-1">
                      <span>Khurda (Bhubaneswar Hub)</span>
                      <span>42% Placements</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div className="bg-gradient-to-r from-orange-500 to-amber-500 h-full rounded-full" style={{ width: '42%' }}></div>
                    </div>
                  </div>

                  {/* Cuttack bar */}
                  <div>
                    <div className="flex justify-between items-center text-xs font-bold text-slate-300 mb-1">
                      <span>Cuttack Hub</span>
                      <span>24% Placements</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-500 to-teal-500 h-full rounded-full" style={{ width: '24%' }}></div>
                    </div>
                  </div>

                  {/* Sundargarh / Rourkela bar */}
                  <div>
                    <div className="flex justify-between items-center text-xs font-bold text-slate-300 mb-1">
                      <span>Sundargarh (Rourkela Hub)</span>
                      <span>18% Placements</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full" style={{ width: '18%' }}></div>
                    </div>
                  </div>

                  {/* Others bar */}
                  <div>
                    <div className="flex justify-between items-center text-xs font-bold text-slate-300 mb-1">
                      <span>Other Districts (Sambalpur, Puri, Balasore)</span>
                      <span>16% Placements</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div className="bg-gradient-to-r from-emerald-500 to-green-500 h-full rounded-full" style={{ width: '16%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Benefits of Collaboration Section */}
        <section className="mb-20">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <span className="text-orange-500 font-black text-xs uppercase tracking-widest mb-2 block font-mono">STARTUP ADVANTAGES</span>
            <h2 className="text-4xl sm:text-3xl font-black text-slate-950 tracking-tight mb-4">
              Benefits of this Collaboration for Startups
            </h2>
            <p className="text-slate-600 font-medium text-base sm:text-sm">
              We empower startups across Odisha with premium tools, ecosystem networking, and direct pipeline distribution to regional educational hubs.
            </p>
          </div>

          <div className="grid grid-cols-3 xsm:grid-cols-12 gap-8 sm:gap-4 xsm:gap-0">
            {/* Benefit 1 */}
            <div className="xsm:col-span-12 p-8   sm:p-4 bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] relative overflow-hidden group hover:border-orange-200 transition-all duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-50/50 rounded-full blur-2xl group-hover:bg-orange-100/50 transition-colors"></div>
              <div className="w-12 h-12 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600 mb-6 group-hover:scale-105 transition-transform">
                <Gift className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-black text-slate-900 mb-3 tracking-tight">3 Months FREE Membership</h4>
              <p className="text-slate-600 font-semibold text-sm leading-relaxed">
                Enjoy 3 Months of FREE Membership with premium Job Sourcing and Resume Database Credits to directly hire the top candidates.
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="xsm:col-span-12 p-8 sm:p-4  bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] relative overflow-hidden group hover:border-indigo-200 transition-all duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-full blur-2xl group-hover:bg-indigo-100/50 transition-colors"></div>
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 mb-6 group-hover:scale-105 transition-transform">
                <Compass className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-black text-slate-900 mb-3 tracking-tight">Grants & Event Updates</h4>
              <p className="text-slate-600 font-semibold text-sm leading-relaxed">
                Secure continuous integrated Access to All Startup Grant schemes, incubator meets, and state Event Updates instantly.
              </p>
            </div>

            {/* Benefit 3 */}
            <div className="xsm:col-span-12 p-8  sm:p-4 bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] relative overflow-hidden group hover:border-teal-200 transition-all duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal-50/50 rounded-full blur-2xl group-hover:bg-teal-100/50 transition-colors"></div>
              <div className="w-12 h-12 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center text-teal-600 mb-6 group-hover:scale-105 transition-transform">
                <GraduationCap className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-black text-slate-900 mb-3 tracking-tight">Direct College Campaigns</h4>
              <p className="text-slate-600 font-semibold text-sm leading-relaxed">
                Share and distribute active Job Posting Campaigns Directly to state-wide Odisha based Colleges for specialized placement hiring.
              </p>
            </div>
          </div>
        </section>

        {/* CTA section under Startup Odisha Hub */}
        <section className="bg-white rounded-3xl p-8 xsm:px-4 sm:p-6 shadow-xl border border-slate-100 text-center relative overflow-hidden max-w-4xl mx-auto">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-orange-500/5 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-brand-500/5 rounded-full blur-2xl"></div>

          <div className="relative z-10 max-w-2xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-4 tracking-tight">
              Are you a Startup from Odisha?
            </h3>

            <p className="text-slate-600 font-medium mb-8 leading-relaxed">
              Empower your core team immediately. Unlock free registration, premium sourcing credits, direct campus drives, and real-time state support.
            </p>

            <div className="flex flex-row xsm:flex-col justify-center items-center gap-4">
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSctU2aSVao2XAp3vHl2J6u6-7dPNy1ReV5K73Rc2XEppZ1jsA/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold px-8 py-4 rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5"
              >
                Post Job / Register
                <ArrowRight className="w-5 h-5" />
              </a>

              <button
                onClick={() => navigate("/")}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold px-8 py-4 rounded-xl transition-all"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};
